import { api } from './api'
import { Task, TaskFormData, TaskStatus, EhrComponentType } from '../types/task'

export const taskService = {
  /**
   * Get all tasks
   */
  getAll: async (): Promise<Task[]> => {
    const response = await api.get<Task[]>('/tasks')
    return response.data
  },

  /**
   * Get a task by ID
   */
  getById: async (id: string): Promise<Task> => {
    const response = await api.get<Task>(`/tasks/${id}`)
    return response.data
  },

  /**
   * Get tasks for a specific patient
   */
  getByPatient: async (patientId: string): Promise<Task[]> => {
    const response = await api.get<Task[]>('/tasks', {
      params: { patientId },
    })
    return response.data
  },

  /**
   * Get tasks linked to an EHR component
   */
  getByEhrComponent: async (type: EhrComponentType, id: string): Promise<Task[]> => {
    const response = await api.get<Task[]>('/tasks', {
      params: { ehrComponentType: type, ehrComponentId: id },
    })
    return response.data
  },

  /**
   * Get tasks by status
   */
  getByStatus: async (status: TaskStatus): Promise<Task[]> => {
    const response = await api.get<Task[]>('/tasks', {
      params: { status },
    })
    return response.data
  },

  /**
   * Get tasks assigned to a specific user
   */
  getAssigned: async (userId: string): Promise<Task[]> => {
    const response = await api.get<Task[]>('/tasks', {
      params: { assignedTo: userId },
    })
    return response.data
  },

  /**
   * Get unassigned tasks
   */
  getUnassigned: async (): Promise<Task[]> => {
    const response = await api.get<Task[]>('/tasks', {
      params: { unassigned: 'true' },
    })
    return response.data
  },

  /**
   * Create a new task
   */
  create: async (data: TaskFormData): Promise<Task> => {
    const response = await api.post<Task>('/tasks', data)
    return response.data
  },

  /**
   * Create a task for a specific patient
   */
  createForPatient: async (patientId: string, data: TaskFormData): Promise<Task> => {
    const response = await api.post<Task>(`/patients/${patientId}/tasks`, {
      ...data,
      patientId,
    })
    return response.data
  },

  /**
   * Update a task
   */
  update: async (id: string, data: Partial<TaskFormData>): Promise<Task> => {
    const response = await api.put<Task>(`/tasks/${id}`, data)
    return response.data
  },

  /**
   * Update task status with optional reason
   */
  updateStatus: async (id: string, status: TaskStatus, reason?: string): Promise<Task> => {
    const response = await api.put<Task>(`/tasks/${id}`, {
      status,
      statusReason: reason,
    })
    return response.data
  },

  /**
   * Assign a task to a provider
   */
  assign: async (id: string, userId: string): Promise<Task> => {
    const response = await api.put<Task>(`/tasks/${id}`, {
      assignedTo: userId,
    })
    return response.data
  },

  /**
   * Unassign a task
   */
  unassign: async (id: string): Promise<Task> => {
    const response = await api.put<Task>(`/tasks/${id}`, {
      assignedTo: null,
    })
    return response.data
  },

  /**
   * Delete a task
   */
  delete: async (id: string): Promise<void> => {
    await api.delete(`/tasks/${id}`)
  },
}
































